import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Table, Badge, Btn, eur, dt } from "../components/common";
import { useAuth } from "../auth";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import AccountPicker from "../components/AccountPicker";
import { useAutoRefresh } from "../lib/autoRefresh";

const NEXT = {
  assistida: ["validacao_tecnica", "validada"],
  criada_online: ["validacao_tecnica", "validada"],
  validacao_tecnica: ["validada", "recusada_tecnica"],
  validada: ["adjudicada"],
  em_provisionamento: ["ativa", "falhou"],
  adjudicada: [],
};

export default function Orders() {
  const { can } = useAuth();
  const [orders, setOrders] = useState([]);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [adjudicar, setAdjudicar] = useState(null);

  const load = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);
  useAutoRefresh(load);

  const transition = async (id, estado, contratoTipo = "standard") => {
    try {
      await api.post(`/orders/${id}/transition?estado=${estado}&contrato_tipo=${contratoTipo}`);
      toast.success(`Encomenda -> ${estado.replace(/_/g, " ")}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const clickTransition = (r, s) => {
    if (s === "adjudicada") {
      setAdjudicar(r);
    } else {
      transition(r.id, s);
    }
  };

  const cols = [
    { key: "organizacao_nome", label: "Cliente", render: (r) => <span className="font-semibold text-navy">{r.organizacao_nome}</span> },
    { key: "origem", label: "Origem", render: (r) => <Badge value={r.origem} /> },
    { key: "itens", label: "Itens", render: (r) => r.itens.map((i) => `${i.quantidade}x ${i.nome}`).join(", ") },
    { key: "desconto_percent", label: "Desc.", render: (r) => `${r.desconto_percent} %` },
    { key: "valor_total", label: "Total", render: (r) => eur(r.valor_total) },
    { key: "estado", label: "Estado", render: (r) => <Badge value={r.estado} /> },
    { key: "criado_em", label: "Criada", render: (r) => dt(r.criado_em) },
    {
      key: "acoes",
      label: "Acoes",
      render: (r) => (
        <div className="flex gap-1 flex-wrap">
          {(NEXT[r.estado] || []).map((s) => (
            <button
              key={s}
              onClick={() => clickTransition(r, s)}
              data-testid={`order-${r.id}-${s}`}
              className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white transition-colors"
            >
              {s.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        overline="Encomendas"
        title="Encomendas"
        action={
          can("encomendas:read") && (
            <Btn onClick={() => setOpen(true)} testid="new-order-btn">
              <Plus size={16} /> Nova encomenda
            </Btn>
          )
        }
      />
      <Table columns={cols} rows={orders} testid="orders-table" />
      {open && <OrderModal plans={plans} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {adjudicar && (
        <AdjudicarModal
          order={adjudicar}
          onClose={() => setAdjudicar(null)}
          onConfirm={async (tipo) => {
            setAdjudicar(null);
            await transition(adjudicar.id, "adjudicada", tipo);
          }}
        />
      )}
    </div>
  );
}

function AdjudicarModal({ order, onClose, onConfirm }) {
  const [tipo, setTipo] = useState("standard");
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="adjudicar-modal">
      <div className="bg-white rounded-xl p-8 w-full max-w-lg">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-navy text-xl font-bold tracking-tight">Adjudicar encomenda</h2>
          <button onClick={onClose}><X className="text-muted2" /></button>
        </div>
        <p className="text-sm text-muted2 mb-4">
          <b>{order.organizacao_nome}</b> - {eur(order.valor_total)}
        </p>
        <label className="block text-sm font-semibold text-navy mb-2">Tipo de contrato</label>
        <div className="space-y-2">
          <label className={`flex items-start gap-3 p-3 rounded border cursor-pointer ${tipo === "standard" ? "border-blue bg-blue/5" : "border-tint"}`}
            data-testid="tipo-standard">
            <input type="radio" checked={tipo === "standard"} onChange={() => setTipo("standard")} className="mt-1" />
            <div>
              <div className="font-semibold text-navy text-sm">Contrato standard</div>
              <div className="text-xs text-muted2">Usa o template default da plataforma. Sera enviado automaticamente para assinatura digital ao provider ativo (DocuSign / Dropbox Sign).</div>
            </div>
          </label>
          <label className={`flex items-start gap-3 p-3 rounded border cursor-pointer ${tipo === "personalizado" ? "border-blue bg-blue/5" : "border-tint"}`}
            data-testid="tipo-personalizado">
            <input type="radio" checked={tipo === "personalizado"} onChange={() => setTipo("personalizado")} className="mt-1" />
            <div>
              <div className="font-semibold text-navy text-sm">Contrato personalizado</div>
              <div className="text-xs text-muted2">Cria contrato em rascunho editavel. Nao envia automaticamente - permite ajustar clausulas antes do envio manual.</div>
            </div>
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={() => onConfirm(tipo)} testid="adjudicar-confirm">Adjudicar</Btn>
        </div>
      </div>
    </div>
  );
}

function OrderModal({ plans, onClose, onSaved }) {
  const [org, setOrg] = useState("");
  const [orgId, setOrgId] = useState("");
  const [nif, setNif] = useState("");
  const [planId, setPlanId] = useState(plans[0]?.id || "");
  const [origem, setOrigem] = useState("assistida");
  const [ref, setRef] = useState("");
  const [desc, setDesc] = useState(0);

  const save = async () => {
    const plan = plans.find((p) => p.id === planId);
    if (!org || !plan) return toast.error("Preencha cliente e plano");
    try {
      await api.post("/orders", {
        origem,
        organizacao_id: orgId || null,
        organizacao_nome: org,
        nif,
        itens: [{ plano_id: plan.id, nome: plan.nome, quantidade: 1, preco: plan.preco }],
        desconto_percent: Number(desc),
        desconto_origem: origem === "assistida" ? "assistida" : "tabela",
        referencia_externa: ref,
        validacao_tecnica_necessaria: true,
      });
      toast.success("Encomenda criada");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="order-modal">
      <div className="bg-white rounded-xl p-8 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-navy text-xl font-bold tracking-tight">Nova encomenda</h2>
          <button onClick={onClose}><X className="text-muted2" /></button>
        </div>
        <label className="block text-sm font-semibold text-navy mb-1">Cliente / Organizacao</label>
        <AccountPicker
          value={org}
          onSelect={(r) => { setOrg(r.nome); setOrgId(r.id); if (r.nif) setNif(r.nif); }}
          onChangeText={(v) => { setOrg(v); setOrgId(""); }}
          testid="order-org"
          className="mb-3"
          placeholder="Escreva para pesquisar contas..."
        />
        <label className="block text-sm font-semibold text-navy mb-1">NIF</label>
        <input value={nif} onChange={(e) => setNif(e.target.value)} className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
        <label className="block text-sm font-semibold text-navy mb-1">Plano</label>
        <select value={planId} onChange={(e) => setPlanId(e.target.value)} data-testid="order-plan" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg">
          {plans.map((p) => <option key={p.id} value={p.id}>{p.nome} - {eur(p.preco)}</option>)}
        </select>
        <label className="block text-sm font-semibold text-navy mb-1">Origem</label>
        <select value={origem} onChange={(e) => setOrigem(e.target.value)} data-testid="order-origem" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg">
          <option value="assistida">Assistida (venda com apoio comercial)</option>
          <option value="online">Online (loja)</option>
        </select>
        <label className="block text-sm font-semibold text-navy mb-1">Referencia externa (opcional)</label>
        <input value={ref} onChange={(e) => setRef(e.target.value)} data-testid="order-ref" placeholder="Ex.: proposta, cotacao..." className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
        <label className="block text-sm font-semibold text-navy mb-1">Desconto (%)</label>
        <input type="number" value={desc} onChange={(e) => setDesc(e.target.value)} className="w-full mb-6 px-3 py-2 border border-tint rounded-lg" />
        <Btn onClick={save} testid="order-save">Criar encomenda</Btn>
      </div>
    </div>
  );
}
